import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, FileText, Home, Info, Landmark, Moon, Sun, Users } from 'lucide-react';
import UserMenu from './UserMenu';

const AppNavbar = ({
  isDarkMode,
  setIsDarkMode,
  showBackButton = false,
  onBackToHome,
  featureControls = {
    home: true,
    about: true,
    bills: true,
    calendar: true,
    politicalProfiles: true,
    localBody: true,
    accounts: true
  }
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '';
  const features = {
    home: true,
    about: true,
    bills: true,
    calendar: true,
    politicalProfiles: true,
    localBody: true,
    accounts: true,
    ...featureControls
  };
  const firstEnabledRoute =
    (features.home && '/') ||
    (features.politicalProfiles && '/politicians') ||
    (features.about && '/about') ||
    (features.bills && '/bills') ||
    (features.calendar && '/calendar') ||
    (features.localBody && '/local-body/warner-robins') ||
    '/';

  const isHome = pathname === '/';
  const isPoliticalProfiles = pathname === '/politicians' || pathname.startsWith('/politicians/');
  const isAbout = pathname === '/about';
  const isBills = pathname.startsWith('/bills');
  const isCalendar = pathname.startsWith('/calendar');
  const isLocalBody = pathname.startsWith('/local-body');

  const navButtonBase =
    'flex items-center justify-center rounded-md font-medium transition-all';

  return (
    <header
      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col space-y-4 md:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {showBackButton && typeof onBackToHome === 'function' && (
                <button
                  onClick={onBackToHome}
                  className="flex items-center px-2 sm:px-2.5 py-1.5 bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg text-xs sm:text-sm"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}

              <Link to={firstEnabledRoute} className="inline-flex items-center">
                <span
                  className="font-bold text-2xl select-none"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="text-[#3b82f6]">Civic</span>
                  <span className="text-[#ef4444]">Duty</span>
                </span>
              </Link>

              <div
                className={`flex items-center rounded-lg p-0.5 sm:p-1 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}
              >
                {features.home && (
                <button
                  onClick={() => navigate('/')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isHome
                      ? 'bg-[#ef4444] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Home className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Home</span>
                </button>
                )}
                {features.about && (
                <button
                  onClick={() => navigate('/about')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isAbout
                      ? 'bg-[#f59e0b] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Info className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">About</span>
                </button>
                )}
                {features.politicalProfiles && (
                <button
                  onClick={() => navigate('/politicians')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isPoliticalProfiles
                      ? 'bg-[#8b5cf6] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Political Profiles</span>
                </button>
                )}
                {features.bills && (
                <button
                  onClick={() => navigate('/bills')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isBills
                      ? 'bg-[#3b82f6] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Bills</span>
                </button>
                )}
                {features.calendar && (
                <button
                  onClick={() => navigate('/calendar')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isCalendar
                      ? 'bg-[#10b981] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
                )}
                {features.localBody && (
                <button
                  onClick={() => navigate('/local-body/warner-robins')}
                  className={`${navButtonBase} px-2 sm:px-3 py-1.5 text-xs sm:text-sm ${
                    isLocalBody
                      ? 'bg-[#6366f1] text-white'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Landmark className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Local</span>
                </button>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {features.accounts && <UserMenu isDarkMode={isDarkMode} />}
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={firstEnabledRoute} className="inline-flex items-center">
              <span
                className="font-bold text-3xl md:text-4xl select-none"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="text-[#3b82f6]">Civic</span>
                <span className="text-[#ef4444]">Duty</span>
              </span>
            </Link>

            <div
              className={`flex items-center rounded-lg p-1 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
            >
              {features.home && (
              <button
                onClick={() => navigate('/')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isHome
                    ? 'bg-[#ef4444] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </button>
              )}
              {features.about && (
              <button
                onClick={() => navigate('/about')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isAbout
                    ? 'bg-[#f59e0b] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Info className="w-4 h-4 mr-2" />
                About
              </button>
              )}
              {features.politicalProfiles && (
              <button
                onClick={() => navigate('/politicians')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isPoliticalProfiles
                    ? 'bg-[#8b5cf6] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Users className="w-4 h-4 mr-2" />
                Political Profiles
              </button>
              )}
              {features.bills && (
              <button
                onClick={() => navigate('/bills')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isBills
                    ? 'bg-[#3b82f6] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Bills
              </button>
              )}
              {features.calendar && (
              <button
                onClick={() => navigate('/calendar')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isCalendar
                    ? 'bg-[#10b981] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Calendar
              </button>
              )}
              {features.localBody && (
              <button
                onClick={() => navigate('/local-body/warner-robins')}
                className={`${navButtonBase} min-w-[120px] px-4 py-2 text-sm ${
                  isLocalBody
                    ? 'bg-[#6366f1] text-white'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Landmark className="w-4 h-4 mr-2" />
                Local Bodies
              </button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            {features.accounts && <UserMenu isDarkMode={isDarkMode} />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AppNavbar;
